/**
 * Output Format Field Component
 * Displays output format selector with integrated schema selection
 */

import React from 'react';
import { Type, Code, FileCode, ExternalLink, Loader2 } from 'lucide-react';
import { FieldSchema, ValidationResult } from '@/config/fieldConfig';
import { Button } from '@/components/ui/Button';
import { ButtonGroup } from '@/components/ui/ButtonGroup';
import { useI18n } from '@/i18n';
import { useOutputSchemas } from '@/features/system-settings/hooks/useOutputSchemas';
import { openInNewTab } from '@/lib/navigation';
import type { OutputSchema } from '@/types/outputSchema';

interface OutputFormatFieldProps {
  name: string;
  value: any;
  onChange: (value: any) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
  appContext?: any;
  /** Callback to change other form fields (for default_schema_key) */
  onFormDataChange?: (path: string, value: unknown) => void;
}

export const OutputFormatField: React.FC<OutputFormatFieldProps> = ({
  value,
  onChange,
  schema,
  disabled = false,
  appContext,
  onFormDataChange,
}) => {
  const { messages, language } = useI18n();

  const outputFormat = value || schema.default || 'default';

  // Resolve label and help from schema (can be string or function)
  const resolvedLabel = typeof schema.label === 'function' ? schema.label() : schema.label;
  const resolvedHelp = typeof schema.help === 'function'
    ? schema.help()
    : typeof schema.help === 'object'
      ? schema.help[language] || schema.help['en'] || ''
      : schema.help;

  // Get current default_schema_key from appContext
  const defaultSchemaKey: string | null = appContext?.default_schema_key || null;

  // Fetch available schemas from centralized table
  const { data, isLoading } = useOutputSchemas({ enabled: true });
  const availableSchemas: OutputSchema[] = data?.schemas || [];

  // Handle schema selection change
  const handleSchemaChange = (schemaKey: string) => {
    if (onFormDataChange) {
      onFormDataChange('default_schema_key', schemaKey || null);
    }
  };

  // Navigate to global schema management
  const handleGoToSchemaSettings = () => {
    openInNewTab('/business-templates', { tab: 'output-schemas' });
  };

  return (
    <div className="space-y-3">
      {/* Label and Format Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {resolvedLabel}
        </label>
        <ButtonGroup
          options={[
            {
              value: 'default',
              label: messages.fields?.output_format?.options?.default || 'Default',
              icon: <Type className="w-4 h-4" />,
            },
            {
              value: 'markdown',
              label: messages.fields?.output_format?.options?.markdown || 'Markdown',
              icon: <Code className="w-4 h-4" />,
            },
            {
              value: 'json',
              label: messages.fields?.output_format?.options?.json || 'JSON',
              icon: <FileCode className="w-4 h-4" />,
            },
          ]}
          value={outputFormat}
          onChange={onChange}
          disabled={disabled}
        />
        <p className="text-xs text-gray-500 mt-1.5">
          {messages.fields?.output_format?.help || resolvedHelp}
        </p>
      </div>

      {/* Schema Selector (only shown when JSON is selected) */}
      {outputFormat === 'json' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              {language === 'zh' ? '输出 Schema' : 'Output Schema'}
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoToSchemaSettings}
              className="text-gray-500 hover:text-gray-700"
              disabled={disabled}
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1" />
              {language === 'zh' ? '管理 Schema' : 'Manage Schemas'}
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : availableSchemas.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 mb-2">
                {language === 'zh'
                  ? '暂无可用的 Schema，请先创建'
                  : 'No schemas available. Please create one first.'}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleGoToSchemaSettings}
                disabled={disabled}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" />
                {language === 'zh' ? '创建 Schema' : 'Create Schema'}
              </Button>
            </div>
          ) : (
            <>
              <select
                value={defaultSchemaKey || ''}
                onChange={(e) => handleSchemaChange(e.target.value)}
                disabled={disabled}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {language === 'zh' ? '-- 选择 Schema --' : '-- Select Schema --'}
                </option>
                {availableSchemas.map((schemaItem) => (
                  <option key={schemaItem.key} value={schemaItem.key}>
                    {schemaItem.name} ({schemaItem.key})
                  </option>
                ))}
              </select>

              {/* Selected Schema Info */}
              {defaultSchemaKey && (
                <div className="text-xs text-gray-500">
                  <span className="font-medium">
                    {language === 'zh' ? 'API 参数: ' : 'API Parameter: '}
                  </span>
                  <code className="bg-white px-1.5 py-0.5 rounded border border-gray-200 font-mono text-blue-700">
                    output_schema_key="{defaultSchemaKey}"
                  </code>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OutputFormatField;
